import React, { useEffect, useState, useRef, useCallback } from 'react';
import { chat as chatApi } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Send, MessageSquare, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

function MessageBubble({ msg, currentUserId }) {
  const isOwn = msg.sender_id === currentUserId;
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl ${isOwn ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
        style={{
          background: isOwn ? '#006080' : 'var(--surface)',
          border: isOwn ? 'none' : '1px solid var(--border)',
          color: isOwn ? 'white' : 'var(--ink)',
        }}
      >
        {!isOwn && (
          <p className="text-xs font-semibold mb-0.5 opacity-70">{msg.sender_name}</p>
        )}
        <p className="text-sm leading-relaxed break-words">{msg.content}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : ''}`} style={isOwn ? {} : { color: 'var(--muted)' }}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}

export default function ChatTab({ projectId, isOperator }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef({});

  const loadMessages = useCallback(async () => {
    try {
      const r = await chatApi.messages(projectId);
      setMessages(r.data);
    } catch {/* ignore */}
  }, [projectId]);

  // Initial load + WebSocket connection
  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let mounted = true;

    loadMessages().finally(() => setLoading(false));

    const connect = () => {
      if (!mounted) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/chat/${projectId}`;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => setConnected(true);
        ws.onclose = () => {
          setConnected(false);
          if (mounted) reconnectTimeout = setTimeout(connect, 2500);
        };
        ws.onerror = () => { try { ws.close(); } catch {/* noop */} };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'message' && data.message) {
              setMessages(prev => {
                if (prev.some(m => m.id === data.message.id)) return prev;
                return [...prev, data.message];
              });
            } else if (data.type === 'typing' && data.user_id !== user?.id) {
              setTypingUsers(prev => prev.includes(data.user_name) ? prev : [...prev, data.user_name]);
              if (typingTimeoutRef.current[data.user_id]) clearTimeout(typingTimeoutRef.current[data.user_id]);
              typingTimeoutRef.current[data.user_id] = setTimeout(() => {
                setTypingUsers(prev => prev.filter(n => n !== data.user_name));
              }, 2000);
            }
          } catch {/* ignore */}
        };
      } catch {
        reconnectTimeout = setTimeout(connect, 2500);
      }
    };

    connect();

    return () => {
      mounted = false;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) { try { ws.close(); } catch {/* noop */} }
    };
  }, [projectId, loadMessages, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const content = input.trim();
    setInput('');
    setSending(true);

    // If connected, send via WS; otherwise fall back to REST
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content }));
      setSending(false);
    } else {
      try {
        const r = await chatApi.send(projectId, content);
        setMessages(prev => [...prev, r.data]);
      } catch {
        toast.error('Failed to send message');
        setInput(content);
      } finally { setSending(false); }
    }
  };

  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: '400px', maxHeight: '600px' }}>
      <div className="flex-shrink-0 flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
          Chat
          <span title={connected ? 'Real-time connected' : 'Disconnected — reconnecting'} data-testid="chat-connection-indicator">
            {connected ? <Wifi size={12} style={{ color: '#3C8A5A' }} /> : <WifiOff size={12} style={{ color: '#B34A3A' }} />}
          </span>
        </h3>
        <span className="text-xs" style={{ color: 'var(--muted)' }}>{messages.length} messages</span>
      </div>

      <div
        className="flex-1 overflow-y-auto rounded-card p-4 mb-3"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', minHeight: '200px' }}
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32">
            <MessageSquare size={32} style={{ color: 'var(--border)', marginBottom: 8 }} />
            <p className="text-sm" style={{ color: 'var(--muted)' }}>No messages yet. Say hello!</p>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <MessageBubble key={msg.id} msg={msg} currentUserId={user?.id} />
            ))}
            {typingUsers.length > 0 && (
              <p className="text-xs italic animate-pulse" style={{ color: 'var(--muted)' }}>
                {typingUsers.join(', ')} typing…
              </p>
            )}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      <form onSubmit={handleSend} className="flex gap-2 flex-shrink-0">
        <input
          className="input-agency text-sm flex-1"
          placeholder="Type a message..."
          value={input}
          onChange={e => { setInput(e.target.value); handleTyping(); }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          className="btn-primary px-4 flex-shrink-0 flex items-center gap-1.5"
          data-testid="chat-send-button"
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
